import { Box, Typography } from '@mui/material';
import { scaleSequentialLog, range } from 'd3';
import React, { useMemo } from 'react';
import {
  birdTAMColorScale,
  interpolateBioRadColorScale,
} from '../utils/colorScales';
import type { Mode } from '../utils/types';

interface Props {
  mode: Mode;
}

const FlySafeLegend: React.FC<Props> = ({ mode }) => {
  const isBirdTAM = mode === 'birdtam';

  const domain: [number, number] = useMemo(
    () => (isBirdTAM ? [0, 8] : [0.5, 1000]),
    [isBirdTAM],
  );

  const scale = useMemo(() => {
    if (isBirdTAM) {
      return birdTAMColorScale;
    }

    return scaleSequentialLog(interpolateBioRadColorScale).domain(domain);
  }, [isBirdTAM, domain]);

  const tickValues = useMemo(() => {
    if (isBirdTAM) {
      return range(domain[0], domain[1] + 1);
    }

    return [0, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
  }, [isBirdTAM, domain]);

  const gradient = useMemo(() => {
    if (isBirdTAM) {
      return undefined;
    }

    // This is the number of different colors in the bioRad scale
    const resolution = 256;
    const stops: string[] = [];

    for (let i = 0; i <= resolution; i += 1) {
      const t = i / resolution;

      // Convert to log-domain value
      const value = domain[0] * (domain[1] / domain[0]) ** t;

      stops.push(`${scale(value)} ${t * 100}%`);
    }

    return `linear-gradient(to right, ${stops.join(',')})`;
  }, [isBirdTAM, scale, domain]);

  // Clamp first and last tick positions
  // Calculate normally for other positions
  const getPosition = (val: number, i: number): number => {
    if (i === 0) {
      return 0;
    }
    if (i === tickValues.length - 1) {
      return 100;
    }

    return (
      ((Math.log(val) - Math.log(domain[0])) /
        (Math.log(domain[1]) - Math.log(domain[0]))) *
      100
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Color bar */}
      <Box
        sx={{
          display: 'flex',
          height: 16,
          borderRadius: 1,
          overflow: 'hidden',
          background: gradient,
        }}
      >
        {isBirdTAM
          ? tickValues.map((val) => (
              <Box
                key={val}
                sx={{
                  flex: 1,
                  backgroundColor: scale(val),
                }}
              />
            ))
          : null}
      </Box>

      {isBirdTAM ? (
        <Box sx={{ display: 'flex', mt: -0.75 }}>
          {tickValues.map((val) => (
            <Box
              key={val}
              sx={{
                flex: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption">{val}</Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            mt: 0.5,
            height: 22,
          }}
        >
          {/* Tick lines (only bioRad) */}
          {tickValues.map((val, i) => {
            const position = getPosition(val, i);
            const isFirst = i === 0;
            const isLast = i === tickValues.length - 1;

            return (
              <Box
                key={`tick-${val}`}
                sx={{
                  position: 'absolute',
                  left: `${position}%`,
                  top: -4,
                  width: '1px',
                  height: 6,
                  backgroundColor: 'rgba(0,0,0,0.6)',
                  // eslint-disable-next-line no-nested-ternary
                  transform: isFirst
                    ? 'translateX(0)'
                    : isLast
                      ? 'translateX(-1px)'
                      : 'translateX(-0.5px)',
                  pointerEvents: 'none',
                }}
              />
            );
          })}

          {/* Tick labels */}
          {tickValues.map((val, i) => {
            const position = getPosition(val, i);
            const isFirst = i === 0;
            const isLast = i === tickValues.length - 1;

            return (
              <Typography
                key={val}
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: `${position}%`,
                  // eslint-disable-next-line no-nested-ternary
                  transform: isFirst
                    ? 'translateX(0)'
                    : isLast
                      ? 'translateX(-100%)'
                      : 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                }}
                variant="caption"
              >
                {val}
              </Typography>
            );
          })}
        </Box>
      )}

      <Typography
        sx={{
          display: 'block',
          textAlign: 'right',
          opacity: 0.7,
          mt: 0.25,
        }}
        variant="caption"
      >
        {isBirdTAM ? 'BirdTAM index' : 'birds/km³'}
      </Typography>
    </Box>
  );
};

export default FlySafeLegend;
